import { motion, useScroll, useTransform } from 'framer-motion';
import { useRef } from 'react';
import { Badge } from '@/components/ui/badge';
import { Calendar, User, ArrowDown } from 'lucide-react';

interface ProjectHeroProps {
    title: string;
    summary: string;
    coverImage: string;
    client?: string;
    year?: string | number;
    disciplines?: string[];
}

export function ProjectHero({
    title,
    summary,
    coverImage,
    client,
    year,
    disciplines
}: ProjectHeroProps) {
    const ref = useRef<HTMLDivElement>(null);
    const { scrollY } = useScroll();
    const y = useTransform(scrollY, [0, 500], [0, 250]);
    const opacity = useTransform(scrollY, [0, 300], [1, 0]);

    return (
        <div ref={ref} className="relative h-[90vh] min-h-[600px] w-full overflow-hidden flex items-center justify-center">
            {/* Background with Parallax */}
            <motion.div
                style={{ y }}
                className="absolute inset-0 z-0"
            >
                <img
                    src={coverImage}
                    alt={title}
                    className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-blue-900/70 via-blue-950/80 to-slate-950/90 backdrop-blur-[2px]" />
                <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-transparent" />
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-blue-500/10 via-transparent to-transparent" />
            </motion.div>

            {/* Content */}
            <motion.div
                style={{ opacity }}
                className="relative z-10 w-[90%] mx-auto px-0 text-center max-w-6xl"
            >
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, ease: "easeOut" }}
                    className="space-y-8"
                >
                    {disciplines && disciplines.length > 0 && (
                        <div className="flex flex-wrap justify-center gap-3 mb-4">
                            {disciplines.map((d) => (
                                <Badge key={d} variant="outline" className="bg-gradient-to-r from-blue-500/10 to-indigo-500/10 text-blue-300 border-blue-500/30 backdrop-blur-md px-4 py-2 text-sm">
                                    {d}
                                </Badge>
                            ))}
                        </div>
                    )}

                    <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold tracking-tight text-white mb-6 drop-shadow-2xl">
                        {title}
                    </h1>

                    <p className="text-xl md:text-2xl lg:text-3xl text-white/80 max-w-4xl mx-auto leading-relaxed">
                        {summary}
                    </p>

                    <div className="flex flex-wrap items-center justify-center gap-8 text-white/70 pt-6">
                        {client && (
                            <div className="flex items-center gap-3 px-4 py-2 bg-white/5 backdrop-blur-sm rounded-full border border-white/10">
                                <User className="h-5 w-5 text-blue-400" />
                                <span className="font-medium">{client}</span>
                            </div>
                        )}
                        {year && (
                            <div className="flex items-center gap-3 px-4 py-2 bg-white/5 backdrop-blur-sm rounded-full border border-white/10">
                                <Calendar className="h-5 w-5 text-indigo-400" />
                                <span className="font-medium">{year}</span>
                            </div>
                        )}
                    </div>
                </motion.div>
            </motion.div>

            {/* Scroll Indicator */}
            <motion.div
                style={{ opacity }}
                animate={{ y: [0, 10, 0] }}
                transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
                className="absolute bottom-10 left-1/2 -translate-x-1/2 z-10"
            >
                <div className="flex flex-col items-center gap-2 text-white/50">
                    <span className="text-xs uppercase tracking-widest">Scroll</span>
                    <ArrowDown className="h-5 w-5" />
                </div>
            </motion.div>
        </div>
    );
}
